import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Thermometer, Drop, Wind, Sun } from "@phosphor-icons/react";
import axios from "axios";
import { useContent } from "@/contexts/ContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultMetrics = [
  {
    icon: Thermometer,
    label: "Global Avg Temp",
    value: "15.2°C",
    change: "+0.8°C",
    trend: "up",
    color: "text-orange-600",
    bg: "bg-orange-50",
  },
  {
    icon: Drop,
    label: "Sea Level Rise",
    value: "3.6mm/yr",
    change: "+0.4mm",
    trend: "up",
    color: "text-sky-600",
    bg: "bg-sky-50",
  },
  {
    icon: Wind,
    label: "CO2 Levels",
    value: "421 ppm",
    change: "+2.3 ppm",
    trend: "up",
    color: "text-stone-600",
    bg: "bg-stone-100",
  },
  {
    icon: Sun,
    label: "UV Index Avg",
    value: "6.2",
    change: "-0.1",
    trend: "down",
    color: "text-amber-600",
    bg: "bg-amber-50",
  },
];

export default function DataSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [metrics, setMetrics] = useState(defaultMetrics);
  const { content } = useContent();
  const data = content?.data || {};
  const label = data.label || "Live Data Feed";
  const title = data.title || "Global Environmental Metrics";
  const subtitle = data.subtitle || "Key indicators tracked in real-time from our global network of monitoring stations.";

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const res = await axios.get(`${API}/monitoring/metrics`);
        if (res.data && res.data.length > 0) {
          setMetrics(
            res.data.map((m, i) => ({
              ...defaultMetrics[i],
              ...m,
            }))
          );
        }
      } catch {
        // fallback to default metrics
      }
    };
    fetchMetrics();
  }, []);

  return (
    <section
      id="data"
      data-testid="data-section"
      className="relative py-16 sm:py-24 lg:py-32 bg-stone-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p className="text-xs tracking-[0.25em] uppercase font-semibold text-emerald-700 mb-3 font-body" data-testid="data-label">
            {label}
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-stone-900" data-testid="data-title">
            {title}
          </h2>
          <p className="mt-4 font-body text-base text-stone-500 max-w-lg mx-auto" data-testid="data-subtitle">
            {subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric, index) => {
            const IconComponent = metric.icon || Thermometer;
            return (
              <motion.div
                key={metric.label}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                className="bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-stone-200 hover:shadow-lg transition-all duration-500 hover:-translate-y-1 group"
                data-testid={`data-metric-${metric.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div
                  className={`w-12 h-12 rounded-2xl ${metric.bg} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300`}
                >
                  <IconComponent size={24} weight="duotone" className={metric.color} />
                </div>
                <p className="font-body text-xs uppercase tracking-wider text-stone-400 mb-1">
                  {metric.label}
                </p>
                <p className="font-heading text-3xl font-semibold text-stone-900">
                  {metric.value}
                </p>
                <div className="mt-3 flex items-center gap-1.5">
                  <span
                    className={`text-xs font-medium ${
                      metric.trend === "up" ? "text-orange-600" : "text-emerald-600"
                    }`}
                  >
                    {metric.change}
                  </span>
                  <span className="text-xs text-stone-400">vs last year</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
